// Tour guidato: steps per ruolo.
// Ogni step: { selector, title, body, navTo?, placement? }
//  - selector: CSS selector dell'elemento da evidenziare (es. '[data-guide="nav-giocatori"]').
//              Se omesso, lo step è "centrato" (nessun evidenziamento, solo testo).
//  - navTo: id pagina da aprire prima di mostrare lo step (la guida chiama setActive).
//  - placement: 'right' (default per voci menu) | 'bottom' | 'left' | 'top' | 'center'.

const benvenuto = (label) => ({
  title: `Benvenuto in Sportly, ${label}`,
  body: 'Questa breve guida ti mostra i moduli principali. Puoi saltarla e riavviarla in qualsiasi momento dal pulsante "?" in alto a destra.',
  placement: 'center',
})

const stepNav = (id, title, body) => ({
  selector: `[data-guide="nav-${id}"]`,
  navTo: id,
  title, body,
  placement: 'right',
})

const stepHeaderGuida = {
  selector: '[data-guide="header-guide"]',
  title: 'Riapri la guida',
  body: 'Trovi sempre questo pulsante "?" in alto per rivedere la guida quando vuoi.',
  placement: 'bottom',
}

// ── ADMIN / SUPERADMIN ──
const tourAdmin = (nome) => [
  benvenuto(nome),
  {
    selector: '[data-guide="sidebar"]',
    title: 'Menu laterale',
    body: 'Da qui accedi a tutti i moduli, raggruppati per area: Panoramica, Rosa, Sportiva, Amministrazione, Comunicazione, Sistema.',
    placement: 'right',
  },
  stepNav('dashboard', 'Dashboard', 'Panoramica della società: numeri chiave, scadenze, prossime partite. Clicca le righe per saltare al dettaglio.'),
  stepNav('squadre', 'Squadre', 'Crea e organizza le squadre della società. Ogni atleta può essere assegnato a una o più squadre.'),
  stepNav('giocatori', 'Atleti', 'Anagrafica completa degli atleti: dati, categoria, documenti, pagamenti, storico presenze.'),
  stepNav('allenatori', 'Allenatori', 'Gestione staff tecnico e abbinamento alle squadre.'),
  stepNav('calendario', 'Calendario', 'Tutti gli eventi della società (partite, allenamenti, tornei). Stampabile per società, solo casa o solo trasferta.'),
  stepNav('presenze', 'Allenamenti', 'Registra le presenze agli allenamenti per squadra. La panchina conta come presente con 0 minuti.'),
  stepNav('convocazioni', 'Convocazioni', 'Convoca gli atleti per le partite, genera la distinta con numeri di maglia e stampa il PDF con il logo della società.'),
  stepNav('tornei', 'Campionati e Tornei', 'Gestione campionati a gironi e tornei con generazione automatica del calendario (gap 2 giorni tra giornate consecutive).'),
  stepNav('statistiche', 'Statistiche atleti', 'Aggregati stagionali: gol, minuti giocati, convocazioni, presenze in campo.'),
  stepNav('pagamenti', 'Pagamenti', 'Rate, scadenze e stato pagamenti degli atleti.'),
  stepNav('documenti', 'Documenti', 'Archivio documenti (certificati medici, iscrizioni, ecc.) con scadenze.'),
  stepNav('compensi', 'Compensi', 'Compensi di allenatori e dirigenti: annuale, rata mensile, flag iscrizione federale / busta paga. Stampa ricevuta dalla riga.'),
  stepNav('comunicazioni', 'Comunicazioni', 'Invio messaggi broadcast a gruppi di destinatari (via WhatsApp o email).'),
  stepNav('stampe', 'Stampe & PDF', 'Documenti stampabili (distinte, convocazioni, calendari). Tutti con filigrana del logo società.'),
  stepNav('utenti', 'Gestione utenti', 'Crea utenti della società e assegna ruoli. Il primo accesso richiede cambio password.'),
  stepNav('permessi', 'Permessi', 'Matrice permessi per ruolo: decidi quali moduli vede ciascun tipo di utente.'),
  stepNav('configurazione', 'Configurazione', 'Parametri società, stagione attiva, anagrafica sportiva (CF, FIGC, affiliazione).'),
  stepHeaderGuida,
]

// ── SEGRETERIA ──
const tourSegreteria = (nome) => [
  benvenuto(nome),
  stepNav('dashboard', 'Dashboard', 'Panoramica della società con scadenze e prossime partite.'),
  stepNav('giocatori', 'Atleti', 'Anagrafica atleti: dati, documenti, pagamenti.'),
  stepNav('allenatori', 'Allenatori', 'Gestione staff tecnico.'),
  stepNav('dirigenti', 'Dirigenti', 'Elenco dirigenti della società.'),
  stepNav('pagamenti', 'Pagamenti', 'Rate, scadenze e stato pagamenti.'),
  stepNav('documenti', 'Documenti', 'Archivio documenti con scadenze.'),
  stepNav('abbigliamento', 'Abbigliamento', 'Gestione kit e forniture.'),
  stepNav('compensi', 'Compensi', 'Compensi di allenatori e dirigenti, con calcolo rata mensile e stampa della ricevuta.'),
  stepNav('email', 'Email', 'Invio email ai contatti, con gestione thread e conversazioni.'),
  stepNav('stampe', 'Stampe & PDF', 'Tutti i documenti stampabili della società.'),
  stepHeaderGuida,
]

// ── DIRIGENTE ──
const tourDirigente = (nome) => [
  benvenuto(nome),
  stepNav('dashboard', 'Dashboard', 'Panoramica sportiva e amministrativa della società.'),
  stepNav('squadre', 'Squadre', 'Tutte le squadre della società.'),
  stepNav('giocatori', 'Atleti', 'Anagrafica atleti con dati, pagamenti e documenti.'),
  stepNav('calendario', 'Calendario', 'Eventi e partite della società.'),
  stepNav('tornei', 'Campionati e Tornei', 'Classifiche e calendari dei tornei.'),
  stepNav('pagamenti', 'Pagamenti', 'Stato pagamenti atleti.'),
  stepNav('documenti', 'Documenti', 'Archivio documenti della società.'),
  stepNav('comunicazioni', 'Comunicazioni', 'Messaggi verso atleti e genitori.'),
  stepHeaderGuida,
]

// ── ALLENATORE ──
const tourAllenatore = (nome) => [
  benvenuto(nome),
  stepNav('presenze', 'Allenamenti', 'La tua pagina principale: registra le presenze agli allenamenti della tua squadra. La panchina conta come presente con 0 minuti.'),
  stepNav('convocazioni', 'Convocazioni', 'Convoca gli atleti per le partite, assegna i numeri di maglia e stampa la distinta.'),
  stepNav('calendario', 'Calendario', 'Partite e allenamenti della tua squadra.'),
  stepNav('giocatori', 'Atleti', 'Vedi la rosa della tua squadra con dati anagrafici essenziali.'),
  stepNav('tornei', 'Campionati e Tornei', 'Calendario e classifica dei tornei a cui partecipa la squadra.'),
  stepNav('statistiche', 'Statistiche atleti', 'Minutaggio, gol e convocazioni dei tuoi atleti.'),
  stepNav('comunicazioni', 'Comunicazioni', 'Comunica con gli atleti e i loro genitori.'),
  stepHeaderGuida,
]

// ── ATLETA ──
const tourAtleta = (nome) => [
  benvenuto(nome),
  stepNav('profilo', 'Il tuo profilo', 'I tuoi dati personali e la squadra a cui appartieni.'),
  stepNav('calendario', 'Calendario', 'Partite e allenamenti in programma.'),
  stepNav('convocazioni', 'Convocazioni', 'Le partite per cui sei stato convocato e il tuo numero di maglia.'),
  stepHeaderGuida,
]

// ── GENITORE ──
const tourGenitore = (nome) => [
  benvenuto(nome),
  stepNav('profilo', 'Profilo di tuo/a figlio/a', 'I dati dell\'atleta e la squadra di appartenenza.'),
  stepNav('pagamenti', 'Pagamenti', 'Stato delle rate e scadenze da pagare.'),
  stepNav('documenti', 'Documenti', 'Documenti da consegnare (certificati medici, iscrizioni) e relative scadenze.'),
  stepNav('comunicazioni', 'Comunicazioni', 'Messaggi ricevuti dalla società o dall\'allenatore.'),
  stepHeaderGuida,
]

// Versione del tour: se cambia, riappare automaticamente anche a chi l'aveva già visto.
// Incrementa solo quando aggiungi/rimuovi moduli importanti.
const GUIDA_VERSION = 1

const getTourForRuolo = (ruolo, nome) => {
  const n = (nome || '').split(' ')[0] || ''
  switch (ruolo) {
    case 'admin':
    case 'superadmin':
      return tourAdmin(n)
    case 'segreteria':
      return tourSegreteria(n)
    case 'dirigente':
      return tourDirigente(n)
    case 'allenatore':
      return tourAllenatore(n)
    case 'atleta':
      return tourAtleta(n)
    case 'genitore':
      return tourGenitore(n)
    default:
      return tourAdmin(n)
  }
}

export { getTourForRuolo, GUIDA_VERSION }
